import { IGame, PhaseNames } from '../concepts/game';
import { ILogger } from '../concepts/logger';
import { StoreSlice } from '../../utils/store-slice';
import { IPlayer } from '../concepts/player';
import { ICountdown } from '../concepts/countdown';
import { ILocations } from '../concepts/locations';
import { IDocs } from '../concepts/docs';
import { IToDos } from '../concepts/to-dos';
import { GetState } from 'zustand';
import { IAvatar } from '../concepts/avatar';
import { IVoting } from '../concepts/voting';
import { remote } from '../../../remote';
import { executors } from './phases';
import IMeetings from '../concepts/meetings';
import GameRecordId from '../../../core/game-record-id.type';

export const create: StoreSlice<
  IGame,
  ILogger &
  IPlayer &
  ICountdown &
  ILocations &
  IDocs &
  IToDos &
  IVoting &
  IAvatar &
  IMeetings
> = (set, get) => {
  return {
    gameId: '--null--',
    gameRecordId: '--null--::-null--',
    gamePlayId: '--null--',
    isInitialized: false,
    isStarted: false,
    isStopped: false,
    isDebugModeOn: false,
    startTime: 0,
    stopTime: 0,

    async init(opts) {
      get().log(`game initialized, «${opts.gamePlayId}»`);
      const info = await get().playerFromGamePlayId(opts.gamePlayId);

      // Checking if no players have been retrieved from `gamePlayId`
      // (also checking the type because I'm not currently sure if an
      // empty array is still a valid option here).
      if (!info?.players?.length && typeof info?.players?.length !== 'number')
        throw new Error("Couldn't retrieve player from gamePlayId!");

      const players = get().players;

      set({
        gameId: info.gameId,
        gameRecordId: info.gameId as GameRecordId,
        playerId: info.playerId,
        team: info.team,
        role: info.role,
        gamePlayId: info.gamePlayId,
        isInitialized: true,
        players: [...new Set([...players, ...info.players])],
      });

      get().initVoting();
      get().resume();
    },

    start(time) {
      if (get().isStarted) return;

      remote
        .startGame(get().gameRecordId, time, get().players)
        .then(() => get().log('game started'))
        .catch(get().error);
    },

    stop() {
      if (get().isStopped) return;

      get().log('game stopped');
      set({ isStopped: true, stopTime: Date.now() });
    },

    resume() {
      get().log(`game resumed: ${get().gameId}`);

      console.log("GAME RECORD ID: ", get().gameRecordId);
      console.log("GAME ID: ", get().gameId);

      // Setup visibility change listener to sync state when page becomes visible
      // This prevents event loss that can happen during WebSocket reconnection
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            console.log('👁️ [Game Store] Page became visible, syncing state...');
            remote.getGameState(get().gameRecordId).then(gameState => {
              if (gameState) {
                console.log('👁️ [Game Store] State synced after visibility change:', gameState);

                // Check if phase changed
                if (gameState.currentPhase && gameState.currentPhase !== get().currentPhase) {
                  console.log('👁️ [Game Store] Phase changed while tab was hidden:', get().currentPhase, '->', gameState.currentPhase);
                  get().gotoPhase(gameState.currentPhase, gameState.currentPhaseStartTime);
                }

                // Check if isStarted changed
                if (gameState.isStarted !== undefined && gameState.isStarted !== get().isStarted) {
                  console.log('👁️ [Game Store] isStarted changed while tab was hidden:', get().isStarted, '->', gameState.isStarted);
                }

                // Update all fields except currentPhase (already handled)
                const { currentPhase, currentPhaseStartTime, ...restState } = gameState;
                set(restState);
              }
            });
          }
        });
      }

      // First, fetch the current game state from the API
      remote.getGameState(get().gameRecordId).then(gameState => {
        console.log("GAME STATE: ", gameState);
        if (gameState) {
          console.log('🎮 [Game Store] Initial game state loaded:', gameState);

          // Set the game state WITHOUT currentPhase first
          const { currentPhase, currentPhaseStartTime, ...restState } = gameState;
          set(restState);

          // IMPORTANT: Reset currentPhase to force executor to run
          // This is needed because the store might be rehydrated from localStorage
          // with the same phase, causing gotoPhase to early return
          set({ currentPhase: '--null--' });

          // Call gotoPhase to trigger phase executors (open locations, etc.)
          get().gotoPhase(currentPhase, currentPhaseStartTime);
        } else {
          console.warn('🎮 [Game Store] Failed to load initial game state');
        }
      });

      // Setup WebSocket subscription for real-time updates
      remote.onGameSnapshot(get().gameRecordId, doc => {
        console.log('🎮 [Game Store] Game snapshot received:', doc);
        get().log(`game snapshot received, ${doc.currentPhase}`);

        // Check for important state changes
        if (doc.isStarted !== undefined && doc.isStarted !== get().isStarted) {
          console.log('🚀 [Game Store] isStarted changed:', get().isStarted, '->', doc.isStarted);
        }

        // Only update phase if it's provided in the event
        if (doc.currentPhase) {
          console.log('🎮 [Game Store] Phase change detected:', get().currentPhase, '->', doc.currentPhase);
          get().gotoPhase(doc.currentPhase, doc.currentPhaseStartTime ?? Date.now());
        }

        // Merge partial update with existing state (don't overwrite with undefined)
        const updates: Partial<typeof doc> = {};
        for (const [key, value] of Object.entries(doc)) {
          if (value !== undefined) {
            (updates as any)[key] = value;
          }
        }

        console.log('🎮 [Game Store] Applying updates:', updates);
        set(updates);
      });

      remote.onAvatarSnapshot(get().gameId, doc => {
        get().log(`avatar snapshot received, ${doc.avatar}`);
        get().selectAvatar(doc.playerId, doc.avatar);
      });

      remote.onMeetingRoomSnapshot(get().gameId, doc => {
        get().log(
          `meeting rooms snapshot received, ${doc.role} -> ${doc.meetingRoomURL}`
        );
        get().setRoleMeetingRoom(doc.role, doc.meetingRoomURL);
      });
    },

    timeElapsed: (now: number = Date.now()) => {
      if (!get().isStarted) return 0;
      if (get().isStopped) return get().stopTime - get().startTime;
      return now - get().startTime;
    },

    /** Chat */
    isChatOpen: false,
    isChatReadable: false,

    openChat() {
      set({ isChatOpen: true, isChatReadable: true });
    },

    closeChat() {
      set({ isChatOpen: false });
    },

    /** Phases */
    currentPhase: '--null--',
    currentPhaseStartTime: 0,
    gotoPhase(phase, time) {
      if (get().currentPhase === phase) return;

      get().log(`entered phase: «${phase}»`);

      set({ currentPhase: phase, currentPhaseStartTime: time });

      executors[phase](get);
    },

    setRoleMeetingRoom(role: string, url: string) {
      const meetingRooms = get().meetingRooms;

      remote
        .setRoleMeetingRoom(get().gameId, role, url)
        .then(() => get().log(`set meeting room "${url}" for "${role}"`))
        .catch(get().error);

      set({
        meetingRooms: {
          ...meetingRooms,
          [role]: url,
        },
      });
    },

    /** Meeting */
    meetingRoom: '',
    currentMeeting: '',
    isMeetingOn: false,
    startMeeting(id: string) {
      set({ isMeetingOn: true, currentMeeting: id });
    },
    closeMeeting() {
      set({ isMeetingOn: false });
    },
    meetingRooms: {
      DS: null,
      DF: null,
      DM: null,
      DP: null,
      PE: null,
      CA: null,
    },
  };
};

export interface IPhaseExecutorFn {
  (get: GetState<any>): void;
}

export type PhaseExecutorMap = Record<PhaseNames, IPhaseExecutorFn>;
